<template>
	<div class="monaco-editor">
		<div
			ref="editorContainer"
			class="monaco-editor__container"
			:class="{
				'monaco-editor__container--disabled': disabled
			}"
		/>
	</div>
</template>

<script>
import * as monaco from 'monaco-editor'
import { JintValidator } from './validation/JintValidator.js'

export default {
	name: 'monaco-editor',
	props: {
		value: {
			type: String,
			required: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: '// Enter your JavaScript mapping code here...\n// Return the object to be sent in the request payload\n\nreturn {\n  // your mapping logic\n}'
		},
		language: {
			type: String,
			default: 'javascript'
		},
		theme: {
			type: String,
			default: 'vs-dark'
		},
		availableFields: {
			type: Array,
			default: () => []
		},
		/**
		 * Enable Jint-specific validation
		 * When true, validates code against Jint runtime constraints
		 */
		enableJintValidation: {
			type: Boolean,
			default: true
		}
	},
	data () {
		return {
			editor: null,
			isInitialized: false,
			jintValidator: null,
			validationTimeout: null
		}
	},
	watch: {
		value (newValue) {
			if (this.editor && this.editor.getValue() !== newValue) {
				const position = this.editor.getPosition()
				this.editor.setValue(newValue || '')
				if (position) {
					this.editor.setPosition(position)
				}
			}
		},
		disabled (newValue) {
			if (this.editor) {
				this.editor.updateOptions({ readOnly: newValue })
			}
		},
		availableFields: {
			handler () {
				this.setupIntelliSense()
			},
			deep: true
		}
	},
	mounted () {
		this.$nextTick(() => {
			this.initializeEditor()
		})
	},
	beforeDestroy () {
		// Clear validation timeout to prevent memory leak
		if (this.validationTimeout) {
			clearTimeout(this.validationTimeout)
			this.validationTimeout = null
		}
		if (this.editor) {
			this.editor.dispose()
			this.editor = null
		}
	},
	methods: {
		async initializeEditor () {
			if (this.isInitialized || !this.$refs.editorContainer) return

			try {
				const container = this.$refs.editorContainer

				// Initialize Jint validator if enabled
				if (this.enableJintValidation) {
					this.jintValidator = new JintValidator()
				}

				this.editor = monaco.editor.create(container, {
					value: this.value || this.placeholder,
					language: this.language,
					theme: this.theme,
					automaticLayout: true,
					minimap: {
						enabled: false
					},
					readOnly: this.disabled,
					lineNumbers: 'on',
					roundedSelection: false,
					scrollBeyondLastLine: false,
					fontSize: 14,
					fontFamily: '\'Cascadia Code\', \'Fira Code\', Consolas, \'Courier New\', monospace',
					tabSize: 2,
					insertSpaces: true,
					wordWrap: 'on',
					wrappingStrategy: 'advanced',
					padding: {
						top: 10,
						bottom: 10
					},
					fixedOverflowWidgets: true
				})

				// Listen for content changes
				this.editor.onDidChangeModelContent(() => {
					const newValue = this.editor?.getValue() || ''
					if (newValue !== this.value) {
						this.$emit('input', newValue)
					}

					// Run Jint validation (debounced)
					if (this.enableJintValidation) {
						this.runJintValidationDebounced()
					}
				})

				this.isInitialized = true

				// Setup IntelliSense for available fields
				this.setupIntelliSense()

				// Run initial validation
				if (this.enableJintValidation) {
					this.runJintValidation()
				}
			} catch (error) {
				console.error('Failed to initialize Monaco Editor:', error)
			}
		},

		setupIntelliSense () {
			// Generate TypeScript declarations for the data object
			// Field names are lowercase to match runtime data object access pattern
			const fieldDeclarations = this.availableFields && this.availableFields.length > 0
				? this.availableFields
					.map(field => {
						const originalFieldName = field.fieldName || field.name
						const fieldName = originalFieldName ? originalFieldName.toLowerCase() : originalFieldName
						const fieldType = this.getTypeScriptType(field.dataType || field.typeFriendlyName)
						const displayName = field.displayName || originalFieldName
						return `  /** ${displayName} (${field.typeFriendlyName || field.dataType || 'any'}) */\n  ${fieldName}: ${fieldType};`
					})
					.join('\n')
				: '  [key: string]: any;'

			const extraLibContent = `
/**
 * Available data fields from the selected source
 * Access database field values with preserved data types
 */
interface DataFields {
${fieldDeclarations}
}

declare const data: DataFields;

/**
 * System functions for date/time, identity, and security operations
 */
interface SystemFunctions {
  // Date/Time values
  /** Current date and time in ISO format: "2024-01-15T10:30:45.123Z" */
  current_datetime: string;
  /** Current date: "2024-01-15" */
  current_date: string;
  /** Current time: "10:30:45" */
  current_time: string;
  /** Unix timestamp in seconds */
  timestamp: number;
  /** Unix timestamp in milliseconds */
  timestamp_ms: number;

  // Date components
  /** Current year: 2024 */
  current_year: number;
  /** Current month (1-12) */
  current_month: number;
  /** Current day (1-31) */
  current_day: number;
  /** Current hour (0-23) */
  current_hour: number;
  /** Current minute (0-59) */
  current_minute: number;
  /** Current weekday: "Monday", "Tuesday", etc. */
  current_weekday: string;
  /** ISO week number (1-53) */
  week_number: number;
  /** Quarter: "Q1", "Q2", "Q3", "Q4" */
  quarter: string;

  // Formatted dates
  /** UTC now in ISO format */
  utc_now: string;
  /** Compact date: "20240115" */
  date_compact: string;
  /** Compact time: "103045" */
  time_compact: string;

  // Identity functions
  /** Generate GUID: "12345678-1234-1234-1234-123456789abc" */
  guid: string;
  /** Short GUID: "AbCdEfGhIjKlMnOp" */
  short_guid: string;
  /** Random GUID (new each access) */
  random_guid: string;
  /** Random number (1-1,000,000) */
  random_number: number;
  /** Secure random nonce: "Kd8Nm2Qw9Rt5" */
  nonce: string;
  /** Request ID: "req_550e8400e29b41d4a716446655440000" */
  request_id: string;

  // Security functions
  /** SHA-256 hash of service ID */
  service_hash: string;
  /** SHA-256 hash of integration system ID */
  integration_hash: string;
  /** SHA-256 hash of request ID */
  request_hash: string;
  /** Generate SHA-256 hash of any string */
  hash(value: string): string;

  // Timestamp utilities
  /** Base64 encoded timestamp */
  base64_timestamp: string;
  /** URL-safe timestamp: "1705314645123_456" */
  url_safe_timestamp: string;
}

declare const system: SystemFunctions;

/**
 * Encoding functions for secure data transformation
 */
interface EncodeFunctions {
  /** Base64 encode: "hello" -> "aGVsbG8=" */
  base64(value: string): string;
  /** URL encode: "hello world!" -> "hello%20world%21" */
  url(value: string): string;
  /** HTML encode: "<script>" -> "&lt;script&gt;" */
  html(value: string): string;
  /** Form encode (spaces to +): "hello world" -> "hello+world" */
  form(value: string): string;
  /** Hexadecimal encode: "hello" -> "68656c6c6f" */
  hex(value: string): string;
}

declare const encode: EncodeFunctions;

/**
 * Decoding functions for data transformation
 */
interface DecodeFunctions {
  /** Base64 decode: "aGVsbG8=" -> "hello" */
  base64(value: string): string;
  /** URL decode: "hello%20world%21" -> "hello world!" */
  url(value: string): string;
  /** HTML decode: "&lt;script&gt;" -> "<script>" */
  html(value: string): string;
  /** Form decode (+ to spaces): "hello+world" -> "hello world" */
  form(value: string): string;
  /** Hexadecimal decode: "68656c6c6f" -> "hello" */
  hex(value: string): string;
}

declare const decode: DecodeFunctions;

/**
 * Configuration access for integration settings
 */
interface ConfigSettings {
  /** Numeric service ID */
  service_id: number;
  /** Integration ID (ServiceId * 1000 + IntegrationSystemId) */
  integration_id: number;
  /** Integration system ID */
  integration_system_id: number;
  /** API type: "rest", "soap", etc. (lowercase) */
  api_type: string;
  /** True if request body contains "json" */
  is_json: boolean;
}

declare const config: ConfigSettings;
`

			// Add the type definitions to Monaco's JavaScript/TypeScript compiler
			try {
				monaco.languages.typescript.javascriptDefaults.setExtraLibs([
					{
						content: extraLibContent,
						filePath: 'ts:filename/data.d.ts'
					}
				])

				// Configure compiler options for Jint ES5.1 compatibility
				monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
					target: monaco.languages.typescript.ScriptTarget.ES5, // Changed from ES2020 for Jint compatibility
					allowNonTsExtensions: true,
					moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
					module: monaco.languages.typescript.ModuleKind.CommonJS,
					noEmit: true,
					esModuleInterop: true,
					allowJs: true,
					checkJs: true // Enable semantic checking for JavaScript
				})

				// Enable diagnostics - only ignore return statement outside function
				// IMPORTANT: 1005 (Expected token) is no longer ignored to catch syntax errors
				// like missing commas, semicolons, and unbalanced braces
				monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
					noSemanticValidation: false,
					noSyntaxValidation: false,
					diagnosticCodesToIgnore: [
						1108, // 'return' statement outside function (needed for template mode)
						2304, // Cannot find name (for our injected objects: data, system, config, encode, decode)
						2339 // Property does not exist (for dynamic data.* fields)
					]
				})
			} catch (error) {
				console.warn('Failed to setup IntelliSense:', error)
			}
		},

		getTypeScriptType (dataType) {
			if (!dataType) return 'any'

			const typeMap = {
				string: 'string',
				text: 'string',
				number: 'number',
				integer: 'number',
				int: 'number',
				float: 'number',
				double: 'number',
				decimal: 'number',
				boolean: 'boolean',
				bool: 'boolean',
				date: 'Date',
				datetime: 'Date',
				timestamp: 'Date',
				array: 'any[]',
				object: 'any',
				json: 'any'
			}

			return typeMap[dataType.toLowerCase()] || 'any'
		},

		/**
		 * Run Jint validation with debouncing to avoid excessive processing
		 */
		runJintValidationDebounced () {
			if (this.validationTimeout) {
				clearTimeout(this.validationTimeout)
			}

			this.validationTimeout = setTimeout(() => {
				this.runJintValidation()
			}, 300) // 300ms debounce
		},

		/**
		 * Run Jint validation and apply markers to Monaco editor
		 */
		runJintValidation () {
			if (!this.editor || !this.jintValidator) return

			const model = this.editor.getModel()
			if (!model) return

			const code = model.getValue()

			// Skip validation for empty code or placeholder
			if (!code || !code.trim() || code === this.placeholder) {
				monaco.editor.setModelMarkers(model, 'jint', [])
				this.$emit('validation-change', {
					hasErrors: false,
					hasWarnings: false,
					blockedPatterns: [],
					es6Features: [],
					suspiciousPatterns: []
				})
				return
			}

			// Run validation
			const result = this.jintValidator.validate(code)

			// Apply markers to Monaco editor
			monaco.editor.setModelMarkers(model, 'jint', result.markers)

			// Emit validation result to parent component
			this.$emit('validation-change', {
				hasErrors: result.hasErrors,
				hasWarnings: result.hasWarnings,
				blockedPatterns: result.blockedPatterns,
				es6Features: result.es6Features,
				suspiciousPatterns: result.suspiciousPatterns
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.monaco-editor {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	min-height: 400px;
}

.monaco-editor__container {
	flex: 1;
	width: 100%;
	min-height: 400px;
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
	overflow: hidden;

	&--disabled {
		opacity: 0.6;
		pointer-events: none;
	}
}
</style>
